/* eslint-disable @typescript-eslint/no-explicit-any */
// src/pages/gameweeks/GameweekTable.tsx
import { useState, useEffect } from "react";
import { Edit, Trash2, Unlock, Lock, Calculator, Users, Link2, RefreshCw, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Gameweek, GameweekStatus, Phase } from "@/types/api.types";
import AssignMatchesModal from "@/pages/gameweeks/AssignMatchesModal";

interface GameweekTableProps {
  gameweeks: Gameweek[];
  isLoading: boolean;
  onEdit: (gw: Gameweek) => void;
  onOpen: (id: number) => void;
  onLock: (id: number, name: string) => void;
  onCalculate: (id: number, name: string) => void;
  onDelete: (id: number) => void;
  isDeleting: boolean;
  onAssignMatches: (id: number, matchIds: number[]) => void;
  isAssigning: boolean;
  onForceRecalculate: (id: number, name: string) => void;
  isRecalculating: boolean;
  onResyncTeams: (id: number, name: string) => void;
  isResyncing: boolean;
}

const STATUS_COLORS: Record<GameweekStatus, string> = {
  UPCOMING: "bg-gray-100 text-gray-700",
  OPEN: "bg-green-100 text-green-700",
  LOCKED: "bg-orange-100 text-orange-700",
  COMPLETED: "bg-blue-100 text-blue-700",
};

const STATUS_ICONS: Record<GameweekStatus, string> = {
  UPCOMING: "⏳",
  OPEN: "✅",
  LOCKED: "🔒",
  COMPLETED: "🏆",
};

const PHASE_LABELS: Record<Phase, string> = {
  GROUP_STAGE: "Group Stage",
  ROUND_OF_32: "Round of 32",
  ROUND_OF_16: "Round of 16",
  QUARTER_FINAL: "Quarter Final",
  SEMI_FINAL: "Semi Final",
  THIRD_PLACE: "Third Place",
  FINAL: "Final",
};

const formatDeadline = (dateStr: string) => {
  const date = new Date(dateStr);
  return {
    date: date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
    time: date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
};

const getTimeRemaining = (deadline: string) => {
  const diff = new Date(deadline).getTime() - Date.now();
  if (diff <= 0) return { text: "Passed", urgent: false, passed: true };
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  if (h > 48)
    return { text: `${Math.floor(h / 24)}d ${h % 24}h`, urgent: false, passed: false };
  if (h > 0) return { text: `${h}h ${m}m`, urgent: h < 3, passed: false };
  if (m > 0) return { text: `${m}m ${s}s`, urgent: true, passed: false };
  return { text: `${s}s`, urgent: true, passed: false };
};

function LiveCountdown({ deadline, status }: { deadline: string; status: GameweekStatus }) {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (status === "COMPLETED" || status === "LOCKED") return;

    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [status]);

  const { text, urgent, passed } = getTimeRemaining(deadline);

  if (status === "COMPLETED" || status === "LOCKED") {
    return <span className="text-sm text-gray-300">—</span>;
  }

  if (passed) {
    return <span className="text-sm font-semibold text-red-600 animate-pulse">Overdue</span>;
  }

  return (
    <span className={`text-sm font-semibold tabular-nums ${urgent ? "text-red-600" : "text-orange-500"}`}>
      {text}
    </span>
  );
}

export default function GameweekTable({
  gameweeks,
  isLoading,
  onEdit,
  onOpen,
  onLock,
  onCalculate,
  onDelete,
  isDeleting,
  onAssignMatches,
  isAssigning,
  onForceRecalculate,
  isRecalculating,
  onResyncTeams,
  isResyncing,
}: GameweekTableProps) {
  const [assignModalGw, setAssignModalGw] = useState<Gameweek | null>(null);
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  const incompleteGameweeks = gameweeks.filter(
    (gw) => gw.status !== "COMPLETED"
  );
  const hasIncompleteGameweeks = incompleteGameweeks.length > 0;

  return (
    <div className="space-y-4">
      {/* Alert banner for incomplete gameweeks */}
      {hasIncompleteGameweeks && gameweeks.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">⚠️</span>
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900">
                Incomplete Gameweeks
              </h3>
              <p className="text-sm text-amber-700 mt-1">
                You have {incompleteGameweeks.length} gameweek(s) that are not
                completed. Complete all existing gameweeks before creating a new
                one.
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {incompleteGameweeks.map((gw) => (
                  <Badge
                    key={gw.id}
                    className={`${STATUS_COLORS[gw.status]} text-xs`}
                  >
                    {gw.name} - {gw.status}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Gameweek</TableHead>
            <TableHead>Phase</TableHead>
            <TableHead>Round</TableHead>
            <TableHead>Deadline</TableHead>
            <TableHead>Time Left</TableHead>
            <TableHead>Teams</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {gameweeks.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={8}
                className="text-center py-12 text-gray-500"
              >
                <div className="flex flex-col items-center gap-2">
                  <span className="text-4xl">📅</span>
                  <p className="font-medium">No gameweeks created yet</p>
                  <p className="text-sm text-gray-400">
                    Click &quot;Create Gameweek&quot; to add the 8 gameweeks for
                    FIFA 2026
                  </p>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            gameweeks.map((gw) => {
              const { date, time } = formatDeadline(gw.deadline);
              const canDelete = gw.status === "UPCOMING";
              const canOpen = gw.status === "UPCOMING";
              const canLock = gw.status === "OPEN";
              const canCalculate = gw.status === "LOCKED";

              const canAssign = true; // assign matches at any gameweek stage
              const canForceRecalculate = gw.status === "LOCKED" || gw.status === "COMPLETED";
              const canResync = gw.status === "OPEN";

              return (
                <TableRow key={gw.id}>
                  {/* Name */}
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{STATUS_ICONS[gw.status]}</span>
                      <span className="font-semibold text-gray-900">
                        {gw.name}
                      </span>
                    </div>
                  </TableCell>

                  {/* Phase */}
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {PHASE_LABELS[gw.phase] ?? gw.phase}
                    </Badge>
                  </TableCell>

                  {/* Round */}
                  <TableCell>
                    <span className="text-sm text-gray-600">
                      Round {gw.roundNumber}
                    </span>
                  </TableCell>

                  {/* Deadline */}
                  <TableCell>
                    <div className="text-sm">
                      <div className="font-medium">{date}</div>
                      <div className="text-gray-400">{time}</div>
                    </div>
                  </TableCell>

                  {/* Time remaining — live countdown */}
                  <TableCell>
                    <LiveCountdown deadline={gw.deadline} status={gw.status} />
                  </TableCell>

                  {/* Teams submitted */}
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm text-blue-600">
                      <Users className="h-3 w-3" />
                      <span className="font-medium">
                        {(gw as any)._count?.gameweekTeams ?? 0}
                      </span>
                    </div>
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <Badge className={STATUS_COLORS[gw.status]}>
                      {gw.status}
                    </Badge>
                  </TableCell>

                  {/* Actions */}
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {/* Edit deadline — only UPCOMING or OPEN */}
                      {(gw.status === "UPCOMING" || gw.status === "OPEN") && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(gw)}
                          title="Edit deadline"
                        >
                          <Edit className="h-4 w-4 text-blue-600" />
                        </Button>
                      )}

                      {/* Open */}
                      {canOpen && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onOpen(gw.id)}
                          className="text-green-600 hover:bg-green-50"
                          title="Open for team selection"
                        >
                          <Unlock className="h-4 w-4" />
                        </Button>
                      )}

                      {/* Lock */}
                      {canLock && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onLock(gw.id, gw.name)}
                          className="text-orange-500 hover:bg-orange-50"
                          title="Lock team selection"
                        >
                          <Lock className="h-4 w-4" />
                        </Button>
                      )}

                      {/* Calculate Points */}
                      {canCalculate && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onCalculate(gw.id, gw.name)}
                          className="text-blue-600 hover:bg-blue-50"
                          title="Calculate points"
                        >
                          <Calculator className="h-4 w-4" />
                        </Button>
                      )}

                      {/* Assign Matches — LOCKED or COMPLETED */}
                      {canAssign && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setAssignModalGw(gw)}
                          disabled={isAssigning}
                          className="text-indigo-600 hover:bg-indigo-50"
                          title="Assign matches to this gameweek"
                        >
                          <Link2 className="h-4 w-4" />
                        </Button>
                      )}

                      {/* Resync Teams — OPEN only (re-clone from base teams, no auto-replace) */}
                      {canResync && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onResyncTeams(gw.id, gw.name)}
                          disabled={isResyncing}
                          className="text-teal-600 hover:bg-teal-50"
                          title="Re-sync team snapshots from base teams (fix auto-replaced players)"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      )}

                      {/* Force Recalculate — LOCKED or COMPLETED */}
                      {canForceRecalculate && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onForceRecalculate(gw.id, gw.name)}
                          disabled={isRecalculating}
                          className="text-purple-600 hover:bg-purple-50"
                          title="Force-recalculate points (fix corrupted data)"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      )}

                      {/* Delete — only UPCOMING */}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(gw.id)}
                          disabled={isDeleting}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {/* Footer summary */}
      {gameweeks.length > 0 && (
        <div className="px-4 py-3 border-t bg-gray-50 text-sm text-gray-600 flex gap-4">
          <span>Total: {gameweeks.length}</span>
          <span className="text-green-600">
            Open: {gameweeks.filter((g) => g.status === "OPEN").length}
          </span>
          <span className="text-orange-500">
            Locked: {gameweeks.filter((g) => g.status === "LOCKED").length}
          </span>
          <span className="text-blue-600">
            Completed:{" "}
            {gameweeks.filter((g) => g.status === "COMPLETED").length}
          </span>
          <span className="text-gray-400">
            Upcoming: {gameweeks.filter((g) => g.status === "UPCOMING").length}
          </span>
        </div>
      )}
    </div>

    {/* Assign Matches Modal */}
    <AssignMatchesModal
      open={!!assignModalGw}
      onOpenChange={(open) => { if (!open) setAssignModalGw(null); }}
      gameweek={assignModalGw}
      isAssigning={isAssigning}
      onAssign={(matchIds) => {
        if (!assignModalGw) return;
        onAssignMatches(assignModalGw.id, matchIds);
        setAssignModalGw(null);
      }}
    />
    </div>
  );
}
